/**
 * Sanitizacja typografii, usuwa „AI-owe” myślniki (— oraz –) z treści.
 *
 * Zasady:
 * - myślnik między cyframi (3–4, 19–20) => łącznik: 3-4, 19-20
 * - myślnik otoczony spacjami => przecinek: "tekst — dalej" => "tekst, dalej"
 * - pozostałe wystąpienia => łącznik
 *
 * Uwaga: adresy URL są pomijane, żeby nie psuć linków.
 */

export const AI_DASHES = /[—–]/;

/** Czy string wygląda na adres URL / ścieżkę zasobu (nie sanitizujemy). */
export function isUrlLike(input: string): boolean {
  return /^(https?:|mailto:|tel:|data:|\/|\.\/|\.\.\/)/.test(input.trim()) || input.includes("://");
}

export function sanitizeDashes(input: string): string {
  if (!AI_DASHES.test(input)) return input;
  const output = input
    .replace(/(\d)\s*[—–]\s*(\d)/g, "$1-$2")
    .replace(/\s+[—–]\s+/g, ", ")
    .replace(/[—–]/g, "-")
    .replace(/\s+,/g, ",")
    .replace(/,{2,}/g, ",");
  recordDiff(input, output);
  return output;
}

/**
 * Polerowanie typograficzne dla tekstu wyświetlanego w DOM:
 * - proste cudzysłowy "..." => polskie drukarskie „...”
 * - twarde spacje po jednoliterowych spójnikach (a, i, o, u, w, z),
 *   żeby nie zostawały samotnie na końcu wiersza
 */
export function polishTypography(input: string): string {
  if (!input) return input;
  let output = input.replace(/"([^"\n]+)"/g, "„$1”");
  output = output.replace(
    /(^|[\s(„»-])([aiouwzAIOUWZ])[ \t]+(?=[0-9A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż„"(])/g,
    "$1$2\u00A0",
  );
  return output;
}

/** Sanitizuje pojedynczy tekst (formularze, CMS, treści redakcyjne). */
export function sanitizeText(input: string): string {
  if (isUrlLike(input)) return input;
  return sanitizeDashes(input);
}

/** Rekurencyjnie sanitizuje wszystkie stringi w obiekcie/tablicy. */
export function sanitizeContent<T>(value: T): T {
  if (typeof value === "string") return sanitizeText(value) as unknown as T;
  if (Array.isArray(value)) return value.map((item) => sanitizeContent(item)) as unknown as T;
  if (value && typeof value === "object") {
    if (value instanceof Date) return value;
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = sanitizeContent(val);
    }
    return out as unknown as T;
  }
  return value;
}

/* ------------------------------------------------------------------ */
/* Podgląd różnic w trybie deweloperskim                               */
/* ------------------------------------------------------------------ */

export type TypographyDiff = { before: string; after: string; count: number };

const diffs = new Map<string, TypographyDiff>();
const listeners = new Set<() => void>();

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
})();

export function recordDiff(before: string, after: string): void {
  if (!isDev || before === after) return;
  const existing = diffs.get(before);
  if (existing) {
    existing.count += 1;
  } else {
    diffs.set(before, { before, after, count: 1 });
  }
  for (const listener of listeners) listener();
}

export function getTypographyDiffs(): TypographyDiff[] {
  return [...diffs.values()];
}

export function clearTypographyDiffs(): void {
  diffs.clear();
  for (const listener of listeners) listener();
}

export function subscribeTypographyDiffs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
